import { memo, useRef, forwardRef } from 'react';
import cc from 'classnames';
import { observer } from 'decorators';
import theme from 'theme';
import { Grid, Typography, Tooltip, Tabs, Tab, List, ListItem } from '@material-ui/core';
import DropDownButton from 'components/dropdown';
import GridContainer from 'components/gridContainer';
import BreadCrumbs from 'components/breadcrumbs';
import NavLink from 'components/navLink';
import router from 'services/router';

import HelpIcon from 'mdi-material-ui/HelpCircleOutline';
import useStyles from './titleSection.styles';

export default observer(function TitleSection({ breadcrumbs, title, description, help, tabs: tbs, preTab, tabPanelId }) {
  const classes = useStyles();
  const lastTab = useRef(new Map());

  const tabs = tbs && tbs.filter(t => t);
  let currentTabIndex;
  if (tabs) {
    currentTabIndex = tabs.findIndex(t => Array.isArray(t.name) ? t.name.includes(router.name) : t.name === router.name);
    if (currentTabIndex < 0) { currentTabIndex = 0; }
    lastTab.current.set(currentTabIndex, router.name);
  }

  return <div className={cc(classes.titleSectionBody, tabs && classes.withTabs)}>
    <GridContainer>
      <Grid container spacing={2} direction="column" className={classes.container}>
        <Help items={help} />
        <Grid item>
          <BreadCrumbs items={breadcrumbs} />
        </Grid>
        <Grid item>
          <Typography id="titleSection-title" paragraph variant="h3" noWrap className={classes.titleSectionTitle}>
            {title}
          </Typography>
          <div id="titleSection-description" className={classes.titleSectionDescription}>
            {description}
          </div>
        </Grid>
        { tabs &&
          <Grid item>
            <Grid container spacing={2} alignItems="center">
              { preTab &&
                <Grid item>
                  { preTab }
                </Grid>
              }
              <Grid item xs className={classes.withTabs}>
                <Tabs variant="scrollable" value={currentTabIndex}>
                  { tabs.map((t, i) => {
                    let { label, name, ...other } = t;
                    if (Array.isArray(name)) {
                      name = lastTab.current.get(i) || name[0];
                    }
                    const isSelected = i === currentTabIndex;
                    return <NavLink key={'tab_' + i} view={Tab} name={name} label={label} aria-controls={isSelected ? tabPanelId : undefined} {...other} />;
                  })}
                </Tabs>
              </Grid>
            </Grid>
          </Grid>
        }
      </Grid>
    </GridContainer>
  </div>;
});

export const Help = memo(forwardRef(function Help({ items }, ref) {
  const classes = useStyles();

  if (theme.brand.hideHelp || !items || !items.length) { return null; }

  return <DropDownButton
    ref={ref}
    color="inherit"
    className={classes.helpButton}
    direction="right"
    buttonContent={
      <span><HelpIcon className={classes.helpButtonIcon} /> <span className={classes.helpText}>Help</span></span>
    }
  >
    <List disablePadding>
      {
        items.map((v, i) => <NavLink key={'drop_' + i} view={ListItem} button component="a" helpLink={v.helpLink}>{v.name}</NavLink>)
      }
    </List>
  </DropDownButton>;
}));

export const PageFab = memo(forwardRef(function PageFab({ children, title, preventCollapse, ...other }, ref) {
  const classes = useStyles();

  return <Grid container justifyContent="center" className={classes.container} spacing={0}>
    <div className={cc(!preventCollapse && classes.fabBase, preventCollapse && classes.fabNotCollapsed)}>
      <Tooltip title={title || ''} classes={{ popper: classes.pageFabTooltip }}>
        <NavLink ref={ref} fab color="secondary" {...other}>
          { children }
        </NavLink>
      </Tooltip>
    </div>
  </Grid>;
}));

export const ItemFab = memo(forwardRef(function ItemFab({ children, title, ...other }, ref) {
  const classes = useStyles();

  const mainChild = Array.isArray(children) ? children[0] : children;
  const restChildren = Array.isArray(children) && children.length > 1 ? children.slice(1) : null;

  return <Grid item xs className={cc(classes.itemFabContainer, 'hidePrint')}>
    <Grid container direction="column">
      <Grid item xs />
      <Grid item>
        <NavLink ref={ref} fab color="secondary" {...other}>
          { title ?
            <Tooltip title={title}>
              { mainChild }
            </Tooltip>
            :
            mainChild
          }
        </NavLink>
        { restChildren &&
          <div className={classes.itemFabEmptyContainer}>
            { restChildren }
          </div>
        }
      </Grid>
      <Grid item xs />
    </Grid>
  </Grid>;
}));